import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { CustomerSelector } from "../../utils/moduleSelectors/customerSelector";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";

export const SUBJECT_TYPE_OPTIONS = [
  { value: "", apiValue: null },
  { value: "standalone", apiValue: "standalone" },
  { value: "User", apiValue: "User" },
  { value: "Customer", apiValue: "Customer" },
  { value: "Project", apiValue: "Project" },
  { value: "Vehicle", apiValue: "Vehicle" },
  { value: "Tool", apiValue: "Tool" },
];

const ENTITY_PICKER_TYPES = new Set(["User", "Customer", "Project"]);

export function buildSubjectFilterParams(filters) {
  const params = {};
  if (filters.query?.trim()) params.query = filters.query.trim();
  if (filters.status) params.status = filters.status;
  if (filters.subjectType === "standalone") {
    params.standalone = "true";
  } else if (filters.subjectType) {
    params.subject_type = filters.subjectType;
    if (filters.subjectId != null && filters.subjectId !== "") {
      params.subject_id = filters.subjectId;
    }
  }
  return params;
}

export default function SubjectLinkFilter({
  filters,
  onChange,
  showStatusFilter = false,
  searchPlaceholder,
}) {
  const { t } = useTranslation();
  const [entityLabel, setEntityLabel] = useState(filters.subjectLabel || "");

  const subjectOptions = useMemo(
    () =>
      SUBJECT_TYPE_OPTIONS.map((opt) => ({
        ...opt,
        label:
          opt.value === ""
            ? t("subjectLinkFilter.all_modules")
            : t(`subjectLinkFilter.types.${opt.value.toLowerCase()}`),
      })),
    [t]
  );

  const handleSubjectTypeChange = (value) => {
    setEntityLabel("");
    onChange({
      ...filters,
      subjectType: value,
      subjectId: null,
      subjectLabel: "",
    });
  };

  const handleEntityChange = (event) => {
    const obj = event?.target?.data?.object || event?.target?.data;
    const id = event?.target?.value ?? obj?.id ?? null;
    const label =
      obj?.displayTitle ||
      obj?.name ||
      obj?.title ||
      event?.target?.data?.primary ||
      "";
    setEntityLabel(label);
    onChange({
      ...filters,
      subjectId: id,
      subjectLabel: label,
    });
  };

  const clearEntity = () => {
    setEntityLabel("");
    onChange({ ...filters, subjectId: null, subjectLabel: "" });
  };

  const showEntityPicker = ENTITY_PICKER_TYPES.has(filters.subjectType);

  return (
    <div className="space-y-3 border-b border-slate-200 px-6 py-5">
      <div className="grid gap-3 lg:grid-cols-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("subjectLinkFilter.module")}
          </label>
          <select
            value={filters.subjectType || ""}
            onChange={(e) => handleSubjectTypeChange(e.target.value)}
            className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
          >
            {subjectOptions.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {showEntityPicker ? (
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t(`subjectLinkFilter.select_${filters.subjectType.toLowerCase()}`)}
            </label>
            <div className="mt-1 flex items-start gap-2">
              <div className="min-w-0 flex-1">
                {filters.subjectType === "User" ? (
                  <EmployeeSelector
                    onChange={handleEntityChange}
                    selected={filters.subjectId ? { id: filters.subjectId, displayTitle: entityLabel } : null}
                    noLabel
                  />
                ) : null}
                {filters.subjectType === "Customer" ? (
                  <CustomerSelector
                    onChange={handleEntityChange}
                    selected={filters.subjectId ? { id: filters.subjectId, displayTitle: entityLabel } : null}
                    noLabel
                  />
                ) : null}
                {filters.subjectType === "Project" ? (
                  <ProjectSelector
                    onChange={handleEntityChange}
                    selected={filters.subjectId ? { id: filters.subjectId, displayTitle: entityLabel } : null}
                    noLabel
                  />
                ) : null}
              </div>
              {filters.subjectId ? (
                <button
                  type="button"
                  onClick={clearEntity}
                  className="mt-1 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  title={t("subjectLinkFilter.clear_entity")}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
          </div>
        ) : null}

        {showStatusFilter ? (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
              {t("Status")}
            </label>
            <select
              value={filters.status || ""}
              onChange={(e) => onChange({ ...filters, status: e.target.value })}
              className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
            >
              <option value="">{t("subjectLinkFilter.all_statuses")}</option>
              <option value="draft">{t("textDocuments.status.draft")}</option>
              <option value="final">{t("textDocuments.status.final")}</option>
            </select>
          </div>
        ) : null}
      </div>

      <input
        type="text"
        value={filters.query || ""}
        onChange={(e) => onChange({ ...filters, query: e.target.value })}
        placeholder={searchPlaceholder || t("subjectLinkFilter.search_placeholder")}
        className="block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
      />
    </div>
  );
}
